import React, { useState } from 'react';
import axios from 'axios';

const VendorRegistrationApp = ({ returnUrl = '/' }) => {
  // Detect if this is an admin context based on current path
  const isAdminContext = window.location.pathname.toLowerCase().includes('/fosstech/');
  const submitUrl = isAdminContext ? '/FossTech/Vendors/Create' : '/Account/RegisterVendorApi';
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [toast, setToast] = useState(null);

  // Form state
  const [formData, setFormData] = useState({
    // Personal Information
    firstName: '',
    lastName: '',
    email: '',
    phoneNumber: '',
    password: '',
    confirmPassword: '',
    referralCode: '',
    
    // Business Information
    vendorName: '',
    contactPerson: '',
    businessEmail: '',
    businessPhone: '',
    alternatePhone: '',
    website: '',
    creditFacility: '',
    description: '',
    
    // Address
    address: '',
    city: '',
    state: '',
    country: 'India',
    postalCode: '',
    
    // Tax & Banking
    gstNumber: '',
    panNumber: '',
    bankAccountNumber: '',
    bankName: '',
    ifscCode: '',
    accountHolderName: '',
    
    // Terms
    agreeToTerms: false,
    agreeToCommission: false,
    subscribeToNewsletter: false
  });

  // File state
  const [files, setFiles] = useState({
    gstCertificate: null,
    panCard: null,
    businessLicense: null,
    logo: null,
    cancelledCheque: null
  });

  const [filePreviews, setFilePreviews] = useState({});

  const totalSteps = 5;

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 5000);
  };

  const handleInputChange = (field, value) => {
    let finalValue = value;
    
    // Auto-filtering based on field type
    if (['firstName', 'lastName', 'contactPerson', 'accountHolderName', 'bankName'].includes(field)) {
      finalValue = value.replace(/[^A-Za-z\s]/g, '');
    } else if (field === 'creditFacility') {
      const numValue = value.replace(/\D/g, '');
      if (numValue !== '' && parseInt(numValue) > 120) {
        finalValue = '120';
      } else {
        finalValue = numValue;
      }
    } else if (['phoneNumber', 'businessPhone', 'alternatePhone', 'postalCode', 'bankAccountNumber'].includes(field)) {
      finalValue = value.replace(/\D/g, '');
    }
    
    setFormData(prev => ({ ...prev, [field]: finalValue }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const handleFileChange = (field, file) => {
    if (file) {
      setFiles(prev => ({ ...prev, [field]: file }));
      
      // Create preview for images
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onloadend = () => {
          setFilePreviews(prev => ({ ...prev, [field]: reader.result }));
        };
        reader.readAsDataURL(file);
      }
      
      if (errors[field]) {
        setErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    }
  };

  const removeFile = (field) => {
    setFiles(prev => ({ ...prev, [field]: null }));
    setFilePreviews(prev => {
      const newPreviews = { ...prev };
      delete newPreviews[field];
      return newPreviews;
    });
  };

  const validateStep = (step) => {
    const newErrors = {};

    if (step === 1) {
      // Personal Information
      if (!formData.firstName.trim()) newErrors.firstName = 'First name is required';
      else if (!/^[A-Za-z]+$/.test(formData.firstName)) newErrors.firstName = 'Only alphabets allowed';
      
      if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required';
      else if (!/^[A-Za-z]+$/.test(formData.lastName)) newErrors.lastName = 'Only alphabets allowed';
      
      if (!formData.email.trim()) newErrors.email = 'Email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email format';
      
      if (!formData.phoneNumber.trim()) newErrors.phoneNumber = 'Phone number is required';
      else if (!/^\d{10}$/.test(formData.phoneNumber)) newErrors.phoneNumber = 'Phone must be 10 digits';
      
      if (!formData.password) newErrors.password = 'Password is required';
      else if (formData.password.length < 6) newErrors.password = 'Password must be at least 6 characters';
      
      if (formData.password !== formData.confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    }

    if (step === 2) {
      // Business Information
      if (!formData.vendorName.trim()) newErrors.vendorName = 'Vendor/Company name is required';
      if (!formData.contactPerson.trim()) newErrors.contactPerson = 'Contact person name is required';
      if (!formData.businessEmail.trim()) newErrors.businessEmail = 'Business email is required';
      else if (!/\S+@\S+\.\S+/.test(formData.businessEmail)) newErrors.businessEmail = 'Invalid email format';
      if (!formData.businessPhone.trim()) newErrors.businessPhone = 'Business phone is required';
      if (!/^\d{10}$/.test(formData.businessPhone)) newErrors.businessPhone = 'Phone must be 10 digits';
      
      if (formData.creditFacility) {
        const days = parseInt(formData.creditFacility);
        if (days < 7 || days > 120) {
          newErrors.creditFacility = 'Credit facility must be between 7 and 120 days';
        }
      }

      if (!formData.address.trim()) newErrors.address = 'Address is required';
      
      if (!formData.city.trim()) newErrors.city = 'City is required';
      else if (!/^[A-Za-z\s]+$/.test(formData.city)) newErrors.city = 'Only alphabets and spaces allowed';
      
      if (!formData.state.trim()) newErrors.state = 'State is required';
      else if (!/^[A-Za-z\s]+$/.test(formData.state)) newErrors.state = 'Only alphabets and spaces allowed';
      
      if (!formData.postalCode.trim()) newErrors.postalCode = 'Postal code is required';
      else if (!/^\d{6}$/.test(formData.postalCode)) newErrors.postalCode = 'Postal code must be 6 digits';
    }

    if (step === 3) {
      // Tax & Banking
      if (!formData.gstNumber.trim()) newErrors.gstNumber = 'GST number is required';
      else if (formData.gstNumber.length !== 15) newErrors.gstNumber = 'GST number must be 15 characters';
      if (!formData.panNumber.trim()) newErrors.panNumber = 'PAN number is required';
      else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNumber)) newErrors.panNumber = 'Invalid PAN format (e.g., ABCDE1234F)';
      if (!formData.bankAccountNumber.trim()) newErrors.bankAccountNumber = 'Bank account number is required';
      if (!formData.bankName.trim()) newErrors.bankName = 'Bank name is required';
      if (!formData.ifscCode.trim()) newErrors.ifscCode = 'IFSC code is required';
      else if (formData.ifscCode.length !== 11) newErrors.ifscCode = 'IFSC code must be 11 characters';
      if (!formData.accountHolderName.trim()) newErrors.accountHolderName = 'Account holder name is required';
    }

    if (step === 4) {
      // Documents
      if (!files.gstCertificate) newErrors.gstCertificate = 'GST certificate is required';
      if (!files.panCard) newErrors.panCard = 'PAN card is required';
      if (!files.cancelledCheque) newErrors.cancelledCheque = 'Cancelled cheque is required';
    }

    if (step === 5) {
      // Terms
      if (!formData.agreeToTerms) newErrors.agreeToTerms = 'You must agree to the terms and conditions';
      if (!formData.agreeToCommission) newErrors.agreeToCommission = 'You must agree to the commission structure';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const nextStep = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, totalSteps));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      showToast('Please fix the errors before continuing', 'error');
    }
  };

  const prevStep = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    console.log('Form submission started');
    console.log('Current Step:', currentStep);
    console.log('FormData:', formData);

    if (!validateStep(5)) {
      console.log('Validation failed for Step 5');
      console.log('Validation Errors:', errors);
      showToast('Please fix the errors before submitting', 'error');
      return;
    }

    console.log('Validation successful, setting loading to true');
    setLoading(true);

    try {
      const submitData = new FormData();

      // Map camelCase to PascalCase for C# ViewModel
      const fieldMapping = {
        firstName: 'FirstName',
        lastName: 'LastName',
        email: 'Email',
        phoneNumber: 'PhoneNumber',
        password: 'Password',
        confirmPassword: 'ConfirmPassword',
        referralCode: 'ReferralCode',
        vendorName: 'VendorName',
        contactPerson: 'ContactPerson',
        businessEmail: 'BusinessEmail',
        businessPhone: 'BusinessPhone',
        alternatePhone: 'AlternatePhone',
        website: 'Website',
        creditFacility: 'CreditFacility',
        description: 'Description',
        address: 'Address',
        city: 'City',
        state: 'State',
        country: 'Country',
        postalCode: 'PostalCode',
        gstNumber: 'GstNumber',
        panNumber: 'PanNumber',
        bankAccountNumber: 'BankAccountNumber',
        bankName: 'BankName',
        ifscCode: 'IfscCode',
        accountHolderName: 'AccountHolderName',
        agreeToTerms: 'AgreeToTerms',
        agreeToCommission: 'AgreeToCommission',
        subscribeToNewsletter: 'SubscribeToNewsletter'
      };

      // Append all form fields with correct casing
      Object.keys(formData).forEach(key => {
        const mappedKey = fieldMapping[key] || key;
        if (typeof formData[key] === 'boolean') {
          submitData.append(mappedKey, formData[key]);
        } else {
          submitData.append(mappedKey, formData[key] || '');
        }
      });

      // Map file names to PascalCase
      const fileMapping = {
        gstCertificate: 'GstCertificate',
        panCard: 'PanCard',
        cancelledCheque: 'CancelledCheque',
        businessLicense: 'BusinessLicense',
        logo: 'Logo'
      };

      // Append files with correct casing
      Object.keys(files).forEach(key => {
        if (files[key]) {
          const mappedKey = fileMapping[key] || key;
          submitData.append(mappedKey, files[key]);
        }
      });

      const response = await axios.post(submitUrl, submitData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      console.log('Response received:', response.data);

      if (response.data.success) {
        showToast(isAdminContext ? 'Vendor created successfully! Redirecting to vendors list...' : 'Registration successful! Redirecting...', 'success');
        setTimeout(() => {
          window.location.href = returnUrl;
        }, 2000);
      } else {
        console.error('Submission failed response:', response.data);
        showToast(response.data.message || 'Registration failed', 'error');
        handleErrorNavigation(response.data.message);
      }
    } catch (error) {
      console.error('Registration error occurred:', error);
      let errorMessage = 'Registration failed. Please try again.';
      
      if (error.response?.data) {
        console.log('Error response data:', error.response.data);
        const data = error.response.data;
        if (data.errors && Array.isArray(data.errors)) {
          errorMessage = data.errors.join(', ');
        } else if (data.message) {
          errorMessage = data.message;
        } else if (typeof data === 'string') {
          errorMessage = data;
        }
      } else {
        errorMessage = error.message;
      }
      
      showToast(errorMessage, 'error');
      handleErrorNavigation(errorMessage);
    } finally {
      console.log('Submission completed, setting loading to false');
      setLoading(false);
    }
  };

  // Navigate to appropriate step based on error message
  const handleErrorNavigation = (errorMessage) => {
    if (!errorMessage) return;

    const message = errorMessage.toLowerCase();
    const newErrors = {};

    // Step 1 errors - Personal Information
    if (message.includes('email') && (message.includes('already') || message.includes('allready') || message.includes('registered'))) {
      newErrors.email = 'Email Id allready registered';
      setErrors(newErrors);
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (message.includes('phone') && (message.includes('already') || message.includes('allready') || message.includes('registered'))) {
      newErrors.phoneNumber = 'Phone number allready registered';
      setErrors(newErrors);
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
    if (message.includes('password')) {
      newErrors.password = errorMessage;
      setErrors(newErrors);
      setCurrentStep(1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Step 2 errors - Business Information
    if (message.includes('business email') || message.includes('vendor') || message.includes('company')) {
      setCurrentStep(2);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Step 3 errors - Tax & Banking
    if (message.includes('gst') || message.includes('pan') || message.includes('bank') || message.includes('ifsc')) {
      setCurrentStep(3);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Step 4 errors - Documents
    if (message.includes('document') || message.includes('certificate') || message.includes('upload')) {
      setCurrentStep(4);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }

    // Step 5 errors - Employee ID & Terms
    if (message.includes('employee') && message.includes('invalid')) {
      newErrors.referralCode = 'Invalid Employee ID';
      setErrors(newErrors);
      setCurrentStep(5);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return;
    }
  };

  const renderStepIndicator = () => (
    <div className="mb-8">
      <div className="flex items-center justify-between">
        {[1, 2, 3, 4, 5].map((step, index) => (
          <React.Fragment key={step}>
            <div className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
              currentStep >= step 
                ? 'bg-blue-600 border-blue-600 text-white' 
                : 'bg-white border-gray-300 text-gray-400'
            }`}>
              {step}
            </div>
            {index < 4 && (
              <div className={`flex-1 h-1 mx-2 ${
                currentStep > step ? 'bg-blue-600' : 'bg-gray-300'
              }`} />
            )}
          </React.Fragment>
        ))}
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-600">
        <span>Personal</span>
        <span>Business</span>
        <span>Tax & Bank</span>
        <span>Documents</span>
        <span>Review</span>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Personal Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">First Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.firstName}
            onChange={(e) => {
              const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
              handleInputChange('firstName', val);
            }}
            onKeyDown={(e) => {
              if (e.key >= '0' && e.key <= '9') e.preventDefault();
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.firstName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter first name"
          />
          {errors.firstName && <p className="text-red-500 text-sm mt-1">{errors.firstName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Last Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.lastName}
            onChange={(e) => {
              const val = e.target.value.replace(/[^A-Za-z\s]/g, '');
              handleInputChange('lastName', val);
            }}
            onKeyDown={(e) => {
              if (e.key >= '0' && e.key <= '9') e.preventDefault();
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.lastName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter last name"
          />
          {errors.lastName && <p className="text-red-500 text-sm mt-1">{errors.lastName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="email"
            value={formData.email}
            onChange={(e) => handleInputChange('email', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.email ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="your@email.com"
          />
          {errors.email && <p className="text-red-500 text-sm mt-1">{errors.email}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Phone Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="tel"
            value={formData.phoneNumber}
            onChange={(e) => {
              const val = e.target.value.replace(/\D/g, '').substring(0, 10);
              handleInputChange('phoneNumber', val);
            }}
            onKeyDown={(e) => {
              if ([46, 8, 9, 27, 13].includes(e.keyCode) || (e.ctrlKey === true) || (e.keyCode >= 35 && e.keyCode <= 39)) return;
              if ((e.shiftKey || (e.keyCode < 48 || e.keyCode > 57)) && (e.keyCode < 96 || e.keyCode > 105)) e.preventDefault();
            }}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.phoneNumber ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="10-digit phone number"
            maxLength="10"
          />
          {errors.phoneNumber && <p className="text-red-500 text-sm mt-1">{errors.phoneNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="password"
            value={formData.password}
            onChange={(e) => handleInputChange('password', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.password ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Minimum 6 characters"
          />
          {errors.password && <p className="text-red-500 text-sm mt-1">{errors.password}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Confirm Password <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="password"
            value={formData.confirmPassword}
            onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.confirmPassword ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Re-enter password"
          />
          {errors.confirmPassword && <p className="text-red-500 text-sm mt-1">{errors.confirmPassword}</p>}
        </div>
      </div>

      <div className="mt-4">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Employee ID (Optional)
          <span className="text-gray-500 text-xs ml-2">Enter employee ID if you have one</span>
        </label>
        <input
          type="text"
          value={formData.referralCode}
          onChange={(e) => handleInputChange('referralCode', e.target.value.toUpperCase())}
          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          placeholder="Enter employee ID"
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Business Information</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Vendor/Company Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.vendorName}
            onChange={(e) => handleInputChange('vendorName', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.vendorName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Your company name"
          />
          {errors.vendorName && <p className="text-red-500 text-sm mt-1">{errors.vendorName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Contact Person <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.contactPerson}
            onChange={(e) => handleInputChange('contactPerson', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.contactPerson ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Contact person name"
          />
          {errors.contactPerson && <p className="text-red-500 text-sm mt-1">{errors.contactPerson}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Email <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="email"
            value={formData.businessEmail}
            onChange={(e) => handleInputChange('businessEmail', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.businessEmail ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="business@company.com"
          />
          {errors.businessEmail && <p className="text-red-500 text-sm mt-1">{errors.businessEmail}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Phone <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="tel"
            value={formData.businessPhone}
            onChange={(e) => handleInputChange('businessPhone', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.businessPhone ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="10-digit phone"
            maxLength="10"
          />
          {errors.businessPhone && <p className="text-red-500 text-sm mt-1">{errors.businessPhone}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Alternate Phone</label>
          <input
            type="tel"
            value={formData.alternatePhone}
            onChange={(e) => handleInputChange('alternatePhone', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Optional"
            maxLength="10"
          />
        </div>

        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Website</label>
          <input
            type="url"
            name="website"
            value={formData.website}
            onChange={(e) => handleInputChange('website', e.target.value)}
            placeholder="https://example.com"
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          />
        </div>

        <div className="md:col-span-1">
          <label className="block text-sm font-medium text-gray-700 mb-1">Credit Facility (Days) (Optional)</label>
          <input
            type="number"
            name="creditFacility"
            min="7"
            max="120"
            value={formData.creditFacility}
            onChange={(e) => handleInputChange('creditFacility', e.target.value)}
            onBlur={(e) => {
              const val = e.target.value;
              if (val && parseInt(val) < 7) {
                handleInputChange('creditFacility', '7');
              }
            }}
            placeholder="Min 7, Max 120 days (Optional)"
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.creditFacility ? 'border-red-500' : 'border-gray-300'}`}
          />
          <p className="text-[10px] text-gray-500 mt-1">Allowed range: 7 to 120 days (Optional)</p>
          {errors.creditFacility && <p className="text-red-500 text-xs mt-1">{errors.creditFacility}</p>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Address <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.address}
            onChange={(e) => handleInputChange('address', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.address ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Street address"
          />
          {errors.address && <p className="text-red-500 text-sm mt-1">{errors.address}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">City <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.city}
            onChange={(e) => handleInputChange('city', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.city ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="City"
          />
          {errors.city && <p className="text-red-500 text-sm mt-1">{errors.city}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">State <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.state}
            onChange={(e) => handleInputChange('state', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.state ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="State"
          />
          {errors.state && <p className="text-red-500 text-sm mt-1">{errors.state}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Country <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.country}
            onChange={(e) => handleInputChange('country', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            placeholder="Country"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Postal Code <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.postalCode}
            onChange={(e) => handleInputChange('postalCode', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.postalCode ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="6-digit pincode"
            maxLength="6"
          />
          {errors.postalCode && <p className="text-red-500 text-sm mt-1">{errors.postalCode}</p>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Business Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => handleInputChange('description', e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            rows="3"
            placeholder="Tell us about your business..."
          />
        </div>
      </div>
    </div>
  );

  const renderStep3 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Tax & Banking Details</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">GST Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.gstNumber}
            onChange={(e) => handleInputChange('gstNumber', e.target.value.toUpperCase())}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.gstNumber ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="15-character GST number"
            maxLength="15"
          />
          {errors.gstNumber && <p className="text-red-500 text-sm mt-1">{errors.gstNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">PAN Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.panNumber}
            onChange={(e) => handleInputChange('panNumber', e.target.value.toUpperCase())}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.panNumber ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="ABCDE1234F"
            maxLength="10"
          />
          {errors.panNumber && <p className="text-red-500 text-sm mt-1">{errors.panNumber}</p>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Bank Account Number <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.bankAccountNumber}
            onChange={(e) => handleInputChange('bankAccountNumber', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.bankAccountNumber ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Bank account number"
          />
          {errors.bankAccountNumber && <p className="text-red-500 text-sm mt-1">{errors.bankAccountNumber}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Bank Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.bankName}
            onChange={(e) => handleInputChange('bankName', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.bankName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Bank name"
          />
          {errors.bankName && <p className="text-red-500 text-sm mt-1">{errors.bankName}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">IFSC Code <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.ifscCode}
            onChange={(e) => handleInputChange('ifscCode', e.target.value.toUpperCase())}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.ifscCode ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="11-character IFSC"
            maxLength="11"
          />
          {errors.ifscCode && <p className="text-red-500 text-sm mt-1">{errors.ifscCode}</p>}
        </div>

        <div className="md:col-span-2">
          <label className="block text-sm font-medium text-gray-700 mb-1">Account Holder Name <span className="text-red-600" style={{ color: 'red' }}>*</span></label>
          <input
            type="text"
            value={formData.accountHolderName}
            onChange={(e) => handleInputChange('accountHolderName', e.target.value)}
            className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 ${errors.accountHolderName ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Name as per bank account"
          />
          {errors.accountHolderName && <p className="text-red-500 text-sm mt-1">{errors.accountHolderName}</p>}
        </div>
      </div>
    </div>
  );

  const FileUploadBox = ({ label, field, required, accept = "image/*,.pdf" }) => (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-2">
        {label} {required && <span className="text-red-600" style={{ color: 'red' }}>*</span>}
      </label>
      
      {!files[field] ? (
        <label className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed rounded-lg cursor-pointer hover:bg-gray-50 transition-colors ${
          errors[field] ? 'border-red-500 bg-red-50' : 'border-gray-300'
        }`}>
          <div className="flex flex-col items-center justify-center pt-5 pb-6">
            <i className="fas fa-cloud-upload-alt text-3xl text-gray-400 mb-2"></i>
            <p className="text-sm text-gray-600">Click to upload or drag and drop</p>
            <p className="text-xs text-gray-500 mt-1">PDF, JPG, PNG (Max 5MB)</p>
          </div>
          <input
            type="file"
            className="hidden"
            accept={accept}
            onChange={(e) => handleFileChange(field, e.target.files[0])}
          />
        </label>
      ) : (
        <div className="relative border-2 border-green-500 rounded-lg p-4 bg-green-50">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              {filePreviews[field] ? (
                <img src={filePreviews[field]} alt="Preview" className="w-16 h-16 object-cover rounded" />
              ) : (
                <i className="fas fa-file-pdf text-3xl text-red-600"></i>
              )}
              <div>
                <p className="text-sm font-medium text-gray-900">{files[field].name}</p>
                <p className="text-xs text-gray-500">{(files[field].size / 1024).toFixed(2)} KB</p>
              </div>
            </div>
            <button
              type="button"
              onClick={() => removeFile(field)}
              className="text-red-600 hover:text-red-800"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>
      )}
      
      {errors[field] && <p className="text-red-500 text-sm mt-1">{errors[field]}</p>}
    </div>
  );

  const renderStep4 = () => (
    <div className="space-y-4">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Upload Documents</h2>
      
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FileUploadBox label="GST Certificate" field="gstCertificate" required />
        <FileUploadBox label="PAN Card" field="panCard" required />
        <FileUploadBox label="Cancelled Cheque" field="cancelledCheque" required />
        <FileUploadBox label="Business License" field="businessLicense" />
        <FileUploadBox label="Company Logo" field="logo" accept="image/*" />
      </div>
      
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
        <div className="flex items-start">
          <i className="fas fa-info-circle text-blue-600 mt-1 mr-3"></i>
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Document Requirements:</p>
            <ul className="list-disc list-inside space-y-1">
              <li>All documents must be clear and readable</li>
              <li>Accepted formats: PDF, JPG, PNG</li>
              <li>Maximum file size: 5MB per document</li>
              <li>GST Certificate, PAN Card, and Cancelled Cheque are mandatory</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );

  const renderStep5 = () => (
    <div className="space-y-6">
      <h2 className="text-2xl font-bold text-gray-900 mb-4">Review & Submit</h2>
      
      {/* Summary */}
      <div className="bg-gray-50 rounded-lg p-6 space-y-4">
        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Personal Information</h3>
          <p className="text-sm text-gray-600">{formData.firstName} {formData.lastName}</p>
          <p className="text-sm text-gray-600">{formData.email} | {formData.phoneNumber}</p>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Business Information</h3>
          <p className="text-sm text-gray-600">{formData.vendorName}</p>
          <p className="text-sm text-gray-600">{formData.address}, {formData.city}, {formData.state} - {formData.postalCode}</p>
          <p className="text-sm text-gray-600">{formData.businessEmail} | {formData.businessPhone}</p>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Tax & Banking</h3>
          <p className="text-sm text-gray-600">GST: {formData.gstNumber}</p>
          <p className="text-sm text-gray-600">PAN: {formData.panNumber}</p>
          <p className="text-sm text-gray-600">Bank: {formData.bankName} ({formData.ifscCode})</p>
        </div>

        <div>
          <h3 className="font-semibold text-gray-900 mb-2">Documents Uploaded</h3>
          <div className="flex flex-wrap gap-2">
            {files.gstCertificate && <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ GST Certificate</span>}
            {files.panCard && <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ PAN Card</span>}
            {files.cancelledCheque && <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ Cancelled Cheque</span>}
            {files.businessLicense && <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ Business License</span>}
            {files.logo && <span className="px-3 py-1 bg-green-100 text-green-800 text-xs rounded-full">✓ Logo</span>}
          </div>
        </div>
      </div>

      {/* Employee ID */}
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Employee ID (Optional)
            <span className="text-gray-500 text-xs ml-2">Enter employee ID if you have one</span>
          </label>
          <input
            type="text"
            value={formData.referralCode}
            onChange={(e) => handleInputChange('referralCode', e.target.value.toUpperCase())}
            maxLength="20"
            className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${errors.referralCode ? 'border-red-500' : 'border-gray-300'}`}
            placeholder="Enter employee ID"
          />
          {errors.referralCode && <p className="text-red-500 text-sm mt-1">{errors.referralCode}</p>}
        </div>
      </div>

      {/* Terms & Conditions */}
      <div className="space-y-4">
        <div className="flex items-start">
          <input
            type="checkbox"
            checked={formData.agreeToTerms}
            onChange={(e) => handleInputChange('agreeToTerms', e.target.checked)}
            className="mt-1 mr-3"
          />
          <label className="text-sm text-gray-700">
            I agree to the <a href="#" className="text-blue-600 hover:underline">Vendor Terms and Conditions</a> *
          </label>
        </div>
        {errors.agreeToTerms && <p className="text-red-500 text-sm">{errors.agreeToTerms}</p>}

        <div className="flex items-start">
          <input
            type="checkbox"
            checked={formData.agreeToCommission}
            onChange={(e) => handleInputChange('agreeToCommission', e.target.checked)}
            className="mt-1 mr-3"
          />
          <label className="text-sm text-gray-700">
            I agree to the Commission Structure (10% on all sales) *
          </label>
        </div>
        {errors.agreeToCommission && <p className="text-red-500 text-sm">{errors.agreeToCommission}</p>}

        <div className="flex items-start">
          <input
            type="checkbox"
            checked={formData.subscribeToNewsletter}
            onChange={(e) => handleInputChange('subscribeToNewsletter', e.target.checked)}
            className="mt-1 mr-3"
          />
          <label className="text-sm text-gray-700">
            Subscribe to newsletter for updates and offers
          </label>
        </div>
      </div>
    </div>
  );

  return (
    <div className={`${isAdminContext ? 'py-4' : 'min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12'} px-4`}>
      {/* Toast Notification */}
      {toast && (
        <div className={`fixed top-4 right-4 z-50 px-6 py-4 rounded-lg shadow-lg ${
          toast.type === 'success' ? 'bg-green-500' : 'bg-red-500'
        } text-white`}>
          <div className="flex items-center space-x-2">
            <i className={`fas ${toast.type === 'success' ? 'fa-check-circle' : 'fa-exclamation-circle'}`}></i>
            <span>{toast.message}</span>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto">
        {/* Header - Only show for non-admin context */}
        {!isAdminContext && (
          <div className="text-center mb-8">
            <h1 className="text-4xl font-bold text-gray-900 mb-2">
              Become a Vendor
            </h1>
          </div>
        )}

        {/* Main Form Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {renderStepIndicator()}

          <form onSubmit={handleSubmit}>
            {/* Step Content */}
            <div className="mb-8">
              {currentStep === 1 && renderStep1()}
              {currentStep === 2 && renderStep2()}
              {currentStep === 3 && renderStep3()}
              {currentStep === 4 && renderStep4()}
              {currentStep === 5 && renderStep5()}
            </div>

            {/* Navigation Buttons */}
            <div className="flex items-center justify-between pt-6 border-t border-gray-200">
              <button
                type="button"
                onClick={prevStep}
                disabled={currentStep === 1 || loading}
                className={`px-6 py-3 rounded-lg font-medium transition-colors ${
                  currentStep === 1 || loading
                    ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
              >
                <i className="fas fa-arrow-left mr-2"></i>
                Previous
              </button>

              <div className="text-sm text-gray-600">
                Step {currentStep} of {totalSteps}
              </div>

              {currentStep < totalSteps ? (
                <button
                  type="button"
                  onClick={nextStep}
                  disabled={loading}
                  className="px-6 py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors disabled:opacity-50"
                >
                  Next
                  <i className="fas fa-arrow-right ml-2"></i>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={loading}
                  className="px-8 py-3 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors disabled:opacity-50 flex items-center"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <i className="fas fa-check mr-2"></i>
                      Submit Registration
                    </>
                  )}
                </button>
              )}
            </div>
          </form>
        </div>

        {/* Help Text - Only show for non-admin context */}
        {!isAdminContext && (
          <div className="text-center mt-6 text-sm text-gray-600">
            Already have an account? <a href="/Account/Login" className="text-blue-600 hover:underline font-medium">Login here</a>
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorRegistrationApp;
